/* eslint-disable sort-keys */
import { FormattedMessage } from 'react-intl'
import PropTypes from 'prop-types'
import { __, filter, not, prop, toLower } from 'ramda'
import { Box, Typography, useMediaQuery } from '@mui/material'
import CheckRoundedIcon from '@mui/icons-material/CheckRounded'
import { useGetScoresQuery, useGetSelfCheckQuizDataQuery } from 'api/statsApi'
import { useMemo } from 'react'
import DomainScore from 'pages/dashboard/components/DomainScore'

const DomainSummary = ({ name, title, domain }) => {
  const isMobile = useMediaQuery(theme => theme.breakpoints.down('sm'))

  const { data: scores = [] } = useGetScoresQuery(__, {
    refetchOnMountOrArgChange: true
  })

  const { data: quizData = [], isFetching } = useGetSelfCheckQuizDataQuery(__, {
    refetchOnMountOrArgChange: true
  })

  // Must return {} while is fetching is true
  // Without this the app crashes with a race condition
  const currentQuiz = useMemo(() => {
    if (isFetching) return {}
    return quizData.find(({ title }) => toLower(title).includes(name))
  }, [quizData, isFetching])

  // Must return [] while is fetching is true
  // Without this the app crashes with a race condition
  const quizResults = useMemo(() => {
    if (isFetching) return []
    return filter(
      result => not(prop('displayAlert', result)),
      prop('results', currentQuiz)
    )
  }, [currentQuiz, isFetching])

  return (
    <Box
      sx={{
        // eslint-disable-next-line sort-keys
        px: { xs: 2, lg: 4 }
      }}
    >
      <Box
        sx={{
          alignItems: 'center',
          display: 'flex',
          gridGap: 4,
          my: 1.5
        }}
      >
        <CheckRoundedIcon />
        <Typography variant="overline">
          <FormattedMessage defaultMessage="your self check" id="iyUt9c" />
        </Typography>
      </Box>
      <Box
        sx={{
          alignItems: { xs: 'flex-start', sm: 'center' },
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          mb: 2
        }}
      >
        <Typography
          color="primary.main"
          sx={{
            textTransform: 'capitalize'
          }}
          variant="h4"
        >
          {domain}
        </Typography>
        <Typography
          color="secondary.dark"
          sx={{
            textTransform: 'capitalize'
          }}
        >
          {!isMobile && <>&nbsp;/</>} {title}
        </Typography>
      </Box>

      <Box
        sx={{
          display: 'grid',
          gridGap: 16,
          gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' }
        }}
      >
        {quizResults.map(subdomain => (
          <DomainScore
            key={prop('title', subdomain)}
            scores={scores}
            subdomain={subdomain}
          />
        ))}
      </Box>
    </Box>
  )
}

DomainSummary.propTypes = {
  domain: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired
}

export default DomainSummary
